import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  fetchOrganizationDashboard,
  fetchStudioNotifications,
  saveOrgMember,
  removeOrgMember,
  transitionWorkflow,
  runContentAutomation,
  fetchStudioAnalytics,
  saveOrganizationProfile,
} from "./contentStudioApi";
import CommunityCreatePostCard from "../Community/CommunityCreatePostCard";
import { fetchSocialProfile } from "../Community/communitySocialApi";
import {
  resolveMediaUrl,
  handleMediaImgError,
} from "../Helper/mediaUrl";
import { getEntityPlatformRegistry } from "../Helper/brandLinks";
import { fetchStudioCapabilities } from "../Helper/studioCapabilities";
import DonationModeSettings from "./DonationModeSettings";
import RecipientPayoutSettings from "../Workspace/RecipientPayoutSettings";
import "../Community/CommunitySocial.css";
import "./ContentStudio.css";

function OrganizationStudioPage(props) {
  const embedded = !!props.embedded;
  const organizationId =
    props.organizationId ||
    (props.match && props.match.params && props.match.params.organizationId) ||
    (typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("organization_id")) ||
    "";

  const [dash, setDash] = useState(null);
  const [notes, setNotes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);
  const [postNote, setPostNote] = useState("");
  const [missionDraft, setMissionDraft] = useState("");
  const [missionNote, setMissionNote] = useState("");
  const [websiteDraft, setWebsiteDraft] = useState("");
  const [emailDraft, setEmailDraft] = useState("");
  const [phoneDraft, setPhoneDraft] = useState("");
  const [addressDraft, setAddressDraft] = useState("");
  const [cityDraft, setCityDraft] = useState("");
  const [stateDraft, setStateDraft] = useState("");
  const [postalDraft, setPostalDraft] = useState("");
  const [countryDraft, setCountryDraft] = useState("");
  const [donationMode, setDonationMode] = useState("internal");
  const [donationUrl, setDonationUrl] = useState("");
  const [socialDraft, setSocialDraft] = useState({});
  const [brandLinkPlatforms, setBrandLinkPlatforms] = useState(
    () => getEntityPlatformRegistry()
  );
  const [profileSaving, setProfileSaving] = useState(false);
  const [canManageTeam, setCanManageTeam] = useState(false);
  const [teamNote, setTeamNote] = useState(null);
  const [teamBusy, setTeamBusy] = useState(false);
  const [memberForm, setMemberForm] = useState({
    email: "",
    role: "editor",
    department: "",
    can_schedule: true,
    can_publish: false,
    can_broadcast: false,
  });

  const section = props.section || "dashboard";
  const libraryHref =
    "/studio/library?organization_id=" + encodeURIComponent(organizationId || "");

  const load = async () => {
    if (!organizationId) {
      setError("Pass organization id via /studio/organization/:organizationId");
      return;
    }
    setError("");
    const body = await fetchOrganizationDashboard(organizationId);
    if (!body.success) {
      setError(body.error || "Unable to load organization studio");
      setDash(null);
      return;
    }
    setDash(body);
    if (body && body.organization) {
      const org = body.organization;
      setMissionDraft(org.description || "");
      setWebsiteDraft(org.website || "");
      setEmailDraft(org.email || "");
      setPhoneDraft(org.phone || "");
      setAddressDraft(org.address || "");
      setCityDraft(org.city || "");
      setStateDraft(org.state || "");
      setPostalDraft(org.postal_code || "");
      setCountryDraft(org.country || "");
      setDonationMode(org.donation_mode === "external" ? "external" : "internal");
      setDonationUrl(org.donation_url || "");
      const social = org.social_links || {};
      const platforms = getEntityPlatformRegistry(org.brand_link_platforms);
      setBrandLinkPlatforms(platforms);
      const nextDraft = {};
      platforms.forEach((platform) => {
        const key = platform.network;
        nextDraft[key] =
          social[key] ||
          (key === "x" ? social.twitter || "" : "") ||
          "";
      });
      setSocialDraft(nextDraft);
    }
    setNotes(await fetchStudioNotifications({ organization_id: organizationId }));
    setAnalytics(await fetchStudioAnalytics({ organization_id: organizationId, hours: 24 }));
  };

  useEffect(() => {
    load();
    fetchSocialProfile().then((res) => setProfile(res.profile));
  }, [organizationId]);

  // Same rule the server enforces for member save / remove: org owner or admin, or platform admin.
  useEffect(() => {
    let cancelled = false;
    fetchStudioCapabilities().then((caps) => {
      if (cancelled) return;
      const membership = ((caps && caps.memberships) || []).find(
        (m) => String(m.organization_id) === String(organizationId)
      );
      setCanManageTeam(
        !!(caps && caps.is_platform_admin) ||
          !!(membership && ["owner", "admin"].includes(membership.role))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const runTeamAction = async (request, successText, onSuccess) => {
    setTeamBusy(true);
    setTeamNote(null);
    let body = null;
    try {
      body = await request();
    } catch (err) {
      body = (err && err.response && err.response.data) || null;
    }
    setTeamBusy(false);
    if (body && body.success) {
      setTeamNote({ kind: "success", text: successText });
      if (onSuccess) onSuccess();
      await load();
      return;
    }
    const msg =
      body && body.error === "forbidden"
        ? "Only organization owners and admins can manage the team."
        : (body && (body.error_messages || body.error)) || "Team update failed. Try again.";
    setTeamNote({ kind: "error", text: String(msg) });
  };

  const onAddMember = (e) => {
    e.preventDefault();
    return runTeamAction(
      () =>
        saveOrgMember(
          Object.assign({}, memberForm, {
            organization_id: organizationId,
            email: memberForm.email.trim(),
          })
        ),
      "Team member saved.",
      () => setMemberForm(Object.assign({}, memberForm, { email: "" }))
    );
  };

  const onRemoveMember = (member) =>
    runTeamAction(
      () =>
        removeOrgMember({
          organization_id: organizationId,
          user_id: member.user_id,
        }),
      "Team member removed."
    );

  const counts = (dash && dash.counts) || {};
  const by = counts.by_workflow || {};

  return (
    <div className={"cstudio" + (embedded ? " cstudio--embedded" : "")}>
      {!embedded ? (
        <>
          <Helmet>
            <title>Organization Studio · TPG</title>
          </Helmet>
          <header className="cstudio__header">
            <div>
              <p className="cstudio-eyebrow">Organization Studio</p>
              <h1>
                {(dash && dash.organization && dash.organization.name) ||
                  "Organization"}
              </h1>
            </div>
            <nav className="cstudio__nav">
              <Link to={libraryHref}>Media Library</Link>
              <Link to={"/studio/timeline"}>Timeline Studio</Link>
              <Link to="/ops">Livestreams</Link>
              <Link to="/community/social">Community</Link>
            </nav>
          </header>
        </>
      ) : null}

      {error && <p className="cstudio-error">{error}</p>}

      {!embedded || section === "dashboard" ? (
        <div className="cstudio-module-grid" style={{ marginBottom: "1.25rem" }}>
          <Link className="cstudio-module-card" to={libraryHref}>
            <strong>Media</strong>
            <span className="cstudio-muted">Library &amp; workflow</span>
          </Link>
          <Link className="cstudio-module-card" to="/ops">
            <strong>Livestreams</strong>
            <span className="cstudio-muted">Live Ops</span>
          </Link>
          <p className="cstudio-muted" style={{ gridColumn: "1 / -1" }}>
            Use Storefront in the workspace sidebar for products, orders, and revenue.
          </p>
        </div>
      ) : null}

      {(!embedded || section === "community") && (
      <section className="cstudio-panel">
        <h2>Publish to Community</h2>
        <p className="cstudio-muted">
          Posts are published as this organization — not as the signed-in manager.
        </p>
        <CommunityCreatePostCard
          profile={profile}
          onProfileUpdate={setProfile}
          organizationId={organizationId ? Number(organizationId) : null}
          entityName={
            (dash && dash.organization && dash.organization.name) || null
          }
          entityAvatar={
            (dash && dash.organization && dash.organization.logo_path) || null
          }
          entityPath={
            dash && dash.organization && dash.organization.slug
              ? "/organization/" + dash.organization.slug
              : null
          }
          compact
          onPosted={(post, meta) => {
            if (meta && meta.optimistic) return;
            if (meta && meta.rollback) {
              setPostNote("Post failed — try again.");
              return;
            }
            setPostNote("Published to Community feed.");
          }}
        />
        {postNote ? <p className="cstudio-muted">{postNote}</p> : null}
      </section>
      )}

      {(!embedded || section === "analytics") && (
      <div className="cstudio-grid">
        <div className="cstudio-stat">
          <strong>{counts.total || 0}</strong>
          <span>Shared media</span>
        </div>
        <div className="cstudio-stat">
          <strong>{counts.members || 0}</strong>
          <span>Team members</span>
        </div>
        <div className="cstudio-stat">
          <strong>{by.in_review || 0}</strong>
          <span>In review</span>
        </div>
        <div className="cstudio-stat">
          <strong>{by.published || 0}</strong>
          <span>Published</span>
        </div>
        <div className="cstudio-stat">
          <strong>{(analytics && analytics.broadcast_events) || 0}</strong>
          <span>Broadcast events (24h)</span>
        </div>
      </div>
      )}

      {(!embedded || section === "team") && (
      <section className="cstudio-panel">
        <h2>Team & permissions</h2>
        <ul className="cstudio-list">
          {((dash && dash.members) || []).map((m) => (
            <li key={m.user_id}>
              <div>
                <strong>{m.name || m.email || "Member"}</strong>
                <div className="cstudio-muted">
                  {m.name && m.email ? m.email + " · " : ""}
                  {m.role}
                  {m.department ? " · " + m.department : ""}
                </div>
              </div>
              <span className="cstudio-muted">
                {m.can_schedule ? "schedule " : ""}
                {m.can_publish ? "publish " : ""}
                {m.can_broadcast ? "broadcast" : ""}
              </span>
              {canManageTeam ? (
                <button
                  type="button"
                  className="cstudio-btn cstudio-btn--ghost"
                  disabled={teamBusy}
                  onClick={() => onRemoveMember(m)}
                >
                  Remove
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        {teamNote ? (
          <p
            className={teamNote.kind === "error" ? "cstudio-error" : "cstudio-muted"}
            role={teamNote.kind === "error" ? "alert" : "status"}
          >
            {teamNote.text}
          </p>
        ) : null}
        {canManageTeam ? (
        <form className="cstudio-toolbar" onSubmit={onAddMember}>
          <input
            type="email"
            placeholder="Email"
            value={memberForm.email}
            onChange={(e) => setMemberForm(Object.assign({}, memberForm, { email: e.target.value }))}
            required
          />
          <select
            value={memberForm.role}
            onChange={(e) => setMemberForm(Object.assign({}, memberForm, { role: e.target.value }))}
          >
            <option value="owner">owner</option>
            <option value="admin">admin</option>
            <option value="editor">editor</option>
            <option value="contributor">contributor</option>
            <option value="viewer">viewer</option>
          </select>
          <input
            placeholder="Department"
            value={memberForm.department}
            onChange={(e) =>
              setMemberForm(Object.assign({}, memberForm, { department: e.target.value }))
            }
          />
          <button type="submit" className="cstudio-btn" disabled={teamBusy}>
            Add / update member
          </button>
        </form>
        ) : null}
      </section>
      )}

      {(!embedded ||
        section === "donations" ||
        section === "settings" ||
        section === "profile" ||
        section === "mission") && (
        <DonationModeSettings
          targetType="organization"
          targetId={organizationId}
          initialMode={donationMode}
          initialUrl={donationUrl}
          onSaved={(body) => {
            if (body && body.data && body.data.target) {
              const t = body.data.target;
              setDonationMode(t.mode === "external" ? "external" : "internal");
              setDonationUrl(t.donation_url || t.external_url || "");
            }
            load();
          }}
        />
      )}

      {(!embedded || section === "settings") && organizationId ? (
        <RecipientPayoutSettings
          payableType="organization"
          payableId={organizationId}
          workspace="organization"
        />
      ) : null}

      {(!embedded || section === "settings" || section === "profile" || section === "mission") && (
      <div className="org-settings">
        <header className="cstudio__header" style={{ marginBottom: "1rem" }}>
          <div>
            <p className="cstudio-eyebrow">Settings</p>
            <h1>Organization Settings</h1>
            <p className="cstudio-muted">
              Branding, public profile, donations, and social links — no Admin required.
            </p>
          </div>
        </header>
        {missionNote ? <p className="cstudio-muted">{missionNote}</p> : null}

        <section className="cstudio-panel">
          <h2>Branding</h2>
          <div className="org-profile-editor">
            <div className="org-profile-editor__cover">
              {dash && dash.organization && dash.organization.cover_image_path ? (
                <img
                  src={resolveMediaUrl(dash.organization.cover_image_path)}
                  alt=""
                  onError={handleMediaImgError}
                />
              ) : (
                <div className="org-profile-editor__cover-empty">Cover image</div>
              )}
              <label className="org-profile-editor__change">
                Change Cover
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) return;
                    setProfileSaving(true);
                    const body = await saveOrganizationProfile(organizationId, {
                      cover_image: file,
                    });
                    setProfileSaving(false);
                    if (body && body.success) {
                      setDash(body);
                      setMissionNote("Cover updated.");
                    } else {
                      setMissionNote((body && body.error) || "Cover update failed.");
                    }
                  }}
                />
              </label>
            </div>

            <div className="org-profile-editor__logo-row">
              <div className="org-profile-editor__logo">
                {dash && dash.organization && dash.organization.logo_path ? (
                  <img
                    src={resolveMediaUrl(dash.organization.logo_path)}
                    alt=""
                    onError={handleMediaImgError}
                  />
                ) : (
                  <i className="fas fa-building" aria-hidden="true" />
                )}
                <label className="org-profile-editor__change org-profile-editor__change--logo">
                  Change Photo
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={async (e) => {
                      const file = e.target.files && e.target.files[0];
                      if (!file) return;
                      setProfileSaving(true);
                      const body = await saveOrganizationProfile(organizationId, {
                        logo: file,
                      });
                      setProfileSaving(false);
                      if (body && body.success) {
                        setDash(body);
                        setMissionNote("Profile photo updated.");
                      } else {
                        setMissionNote((body && body.error) || "Photo update failed.");
                      }
                    }}
                  />
                </label>
              </div>
              <div>
                <strong>
                  {(dash && dash.organization && dash.organization.name) ||
                    "Organization"}
                </strong>
                <div className="cstudio-muted">
                  @{(dash && dash.organization && dash.organization.slug) || "handle"}
                </div>
                <p className="cstudio-muted" style={{ marginTop: 6 }}>
                  Name and handle are managed with platform identity tools and shown
                  read-only here.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="cstudio-panel">
          <h2>Public Profile</h2>
          <label className="cstudio-muted" htmlFor="org-mission">
            Mission / About
          </label>
          <textarea
            id="org-mission"
            rows={5}
            className="cstudio-input"
            style={{ width: "100%" }}
            value={missionDraft}
            onChange={(e) => setMissionDraft(e.target.value)}
          />
          <div className="cstudio-toolbar" style={{ flexWrap: "wrap", marginTop: 8 }}>
            <input
              placeholder="Website"
              value={websiteDraft}
              onChange={(e) => setWebsiteDraft(e.target.value)}
            />
            <input
              placeholder="Email"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
            />
            <input
              placeholder="Phone"
              value={phoneDraft}
              onChange={(e) => setPhoneDraft(e.target.value)}
            />
            <input
              placeholder="Address"
              value={addressDraft}
              onChange={(e) => setAddressDraft(e.target.value)}
            />
            <input
              placeholder="City"
              value={cityDraft}
              onChange={(e) => setCityDraft(e.target.value)}
            />
            <input
              placeholder="State"
              value={stateDraft}
              onChange={(e) => setStateDraft(e.target.value)}
            />
            <input
              placeholder="Postal code"
              value={postalDraft}
              onChange={(e) => setPostalDraft(e.target.value)}
            />
            <input
              placeholder="Country"
              value={countryDraft}
              onChange={(e) => setCountryDraft(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="cstudio-btn"
            style={{ marginTop: 10 }}
            disabled={profileSaving}
            onClick={async () => {
              setProfileSaving(true);
              const body = await saveOrganizationProfile(organizationId, {
                description: missionDraft,
                website: websiteDraft,
                email: emailDraft,
                phone: phoneDraft,
                address: addressDraft,
                city: cityDraft,
                state: stateDraft,
                postal_code: postalDraft,
                country: countryDraft,
              });
              setProfileSaving(false);
              if (body && body.success) {
                setDash(body);
                setMissionNote("Public profile saved.");
              } else {
                setMissionNote((body && body.error) || "Save failed.");
              }
            }}
          >
            Save public profile
          </button>
        </section>

        <section className="cstudio-panel">
          <h2>Social Links</h2>
          <div className="cstudio-toolbar" style={{ flexWrap: "wrap" }}>
            {(brandLinkPlatforms.length
              ? brandLinkPlatforms
              : getEntityPlatformRegistry()
            ).map((platform) => (
              <input
                key={platform.network}
                placeholder={(platform.display_name || platform.network) + " URL"}
                value={socialDraft[platform.network] || ""}
                onChange={(e) =>
                  setSocialDraft(
                    Object.assign({}, socialDraft, {
                      [platform.network]: e.target.value,
                    })
                  )
                }
              />
            ))}
          </div>
          <button
            type="button"
            className="cstudio-btn"
            style={{ marginTop: 10 }}
            disabled={profileSaving}
            onClick={async () => {
              setProfileSaving(true);
              const body = await saveOrganizationProfile(organizationId, {
                social_links: socialDraft,
              });
              setProfileSaving(false);
              if (body && body.success) {
                setDash(body);
                if (body.organization) {
                  const social = body.organization.social_links || {};
                  const platforms = getEntityPlatformRegistry(
                    body.organization.brand_link_platforms
                  );
                  setBrandLinkPlatforms(platforms);
                  const nextDraft = {};
                  platforms.forEach((platform) => {
                    nextDraft[platform.network] =
                      social[platform.network] || "";
                  });
                  setSocialDraft(nextDraft);
                }
                setMissionNote("Social links saved.");
              } else {
                setMissionNote((body && body.error) || "Social links save failed.");
              }
            }}
          >
            Save social links
          </button>
        </section>

        <section className="cstudio-panel">
          <h2>Visibility</h2>
          <ul className="cstudio-list">
            <li>
              <strong>Verified</strong>
              <span className="cstudio-muted">
                {(dash && dash.organization && dash.organization.is_verified)
                  ? "Yes"
                  : "No"}{" "}
                (read-only)
              </span>
            </li>
            <li>
              <strong>Partner</strong>
              <span className="cstudio-muted">
                {(dash && dash.organization && dash.organization.is_partner)
                  ? "Yes"
                  : "No"}{" "}
                (read-only)
              </span>
            </li>
            <li>
              <strong>Official</strong>
              <span className="cstudio-muted">
                {(dash && dash.organization && dash.organization.is_official)
                  ? "Yes"
                  : "No"}{" "}
                (read-only)
              </span>
            </li>
            <li>
              <strong>Featured</strong>
              <span className="cstudio-muted">
                {(dash && dash.organization && dash.organization.is_featured)
                  ? "Yes"
                  : "No"}{" "}
                (read-only)
              </span>
            </li>
          </ul>
        </section>
      </div>
      )}

      {!embedded ? (
      <>
      <section className="cstudio-panel">
        <h2>Shared media lifecycle</h2>
        <div className="cstudio-toolbar">
          <button
            type="button"
            className="cstudio-btn cstudio-btn--ghost"
            onClick={() => runContentAutomation({ organization_id: organizationId }).then(load)}
          >
            Run workflow automation
          </button>
          <Link className="cstudio-btn" to={libraryHref}>
            Open Media Library
          </Link>
        </div>
        <ul className="cstudio-list">
          {((dash && dash.recent) || []).map((item) => (
            <li key={item.media_asset_key || item.admin_video_id}>
              <div>
                <strong>{item.title}</strong>
                <div>
                  <span
                    className={
                      "cstudio-badge cstudio-badge--" +
                      ((item.workflow && item.workflow.status) || "draft")
                    }
                  >
                    {(item.workflow && item.workflow.status) || "draft"}
                  </span>
                </div>
              </div>
              <div className="cstudio-workflow">
                {["in_review", "approved", "published", "archived"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="cstudio-btn cstudio-btn--ghost"
                    onClick={() =>
                      transitionWorkflow({
                        admin_video_id: item.admin_video_id,
                        status: s,
                        organization_id: organizationId,
                      }).then(load)
                    }
                  >
                    {s}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="cstudio-panel">
        <h2>Notifications</h2>
        <ul className="cstudio-list">
          {notes.map((n) => (
            <li key={n.id || n.title}>
              <strong>{n.title || n.type}</strong>
              <span className="cstudio-muted">{n.body || n.message}</span>
            </li>
          ))}
        </ul>
      </section>
      </>
      ) : null}
    </div>
  );
}

export default OrganizationStudioPage;
